import React from 'react';
import PropTypes from 'prop-types';

import TableMFH from 'app/shared/components/TableMFH';
import AddressColumn from 'app/shared/components/Analytics/AddressColumn';
import HeaderTooltip from 'app/shared/components/Analytics/HeaderTooltip';

// Multibrand-UI Components
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Container from '@zg-rentals/multibrand-ui/lib/Container';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Text from '@zg-rentals/multibrand-ui/lib/Text';

import numberUtils from 'app/shared/utils/numberUtils';

const propTypes = {
    coreProductInvoiceModel: PropTypes.object.isRequired,
    isScreenMobile: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    onPropertyClick: PropTypes.func.isRequired,
};

function Error({ message }) {
    return (
        <Container>
            <Row>
                <Col>
                    <Text className="error">
                        {message}
                    </Text>
                </Col>
            </Row>
        </Container>
    );
}

class AllSpendTable extends React.Component {
    getCost(cost) {
        return numberUtils.asCurrency(cost, '$0,0.00');
    }

    getCostWithDefault(cost) {
        return numberUtils.asCurrencyWithDefault(cost, '-');
    }

    getTotal(total) {
        return total !== undefined ? total : '-';
    }

    addressColumnRenderer = data => (
        <AddressColumn
            index={data.index}
            listing={data.listing}
            listingKey={data.listingKey}
            onClick={data.listing.hasAccess ? this.props.onPropertyClick : undefined}
            useAddress={data.activeDays > 0}
        />
    );

    getRowList(coreProductInvoiceModel, isMobile, hasLeadData, hasClickData, hasLeaseData, hasWaitlistData,
        hasBoostData) {
        const invoices = Object.values(coreProductInvoiceModel.listingInvoices);
        const listings = coreProductInvoiceModel.propertyDetails;
        return invoices.map((invoice, index) => {
            const {
                feedListingId,
                feedId,
                numPhoneLeads,
                numEmailLeads,
                billableClickCount,
                leaseCount,
                coreProductCost,
                waitlistedDays,
                waitlistCost,
                boostCost,
                totalCost,
                isClickProduct,
                isLeadProduct,
                isLeaseProduct,
                isFlatFeeProduct,
                dailyRate,
                activeDays,
                unitName,
            } = invoice;
            const listingKey = `${feedListingId}_${feedId}`;
            const listing = listings[listingKey] || {};
            const rowData = {
                listingKey,
                listing: { ...listing, unit: unitName },
                propertyName: activeDays > 0 ? listing.street : (listing.name || listing.street),
                index,
            };

            if (!isMobile) {
                if (hasLeadData) {
                    rowData.phoneLeads = (isLeadProduct || isFlatFeeProduct) ? numPhoneLeads : undefined;
                    rowData.emailLeads = (isLeadProduct || isFlatFeeProduct) ? numEmailLeads : undefined;
                }
                if (hasClickData) {
                    rowData.totalClicks = (isClickProduct || isFlatFeeProduct)
                        ? billableClickCount : undefined;
                }
                if (hasLeaseData) {
                    rowData.totalLeases = (isLeaseProduct || isFlatFeeProduct) ? leaseCount : undefined;
                }
                if (hasLeadData || hasClickData || hasLeaseData) {
                    rowData.coreProductCost = coreProductCost;
                }
                if (hasWaitlistData) {
                    rowData.waitlistedDays = waitlistedDays > 0 ? waitlistedDays : undefined;
                    rowData.waitlistCost = waitlistedDays > 0 ? waitlistCost : undefined;
                }
                if (hasBoostData) {
                    rowData.boostCost = boostCost;
                }
            }
            rowData.activeDays = activeDays;
            rowData.dailyRate = dailyRate;
            rowData.totalCost = totalCost;

            return rowData;
        });
    }

    createColumnConfig = (hasLeadData, hasClickData, hasLeaseData, hasWaitlistData,
        hasBoostData, rocketshipAvailable) => {
        const { isScreenMobile } = this.props;
        if (isScreenMobile) {
            return [
                {
                    dataKey: 'propertyName',
                    width: 9,
                    header: 'Property',
                    isSortable: true,
                    renderer: this.addressColumnRenderer,
                },
                {
                    dataKey: 'totalCost',
                    width: 3,
                    header: 'Total Cost',
                    align: 'right',
                    isSortable: true,
                    renderer: data => this.getCostWithDefault(data.totalCost),
                },
            ];
        }
        const columnConfig = [

        ];
        if (hasLeadData) {
            columnConfig.push({
                dataKey: 'phoneLeads',
                width: 1,
                header: 'Phone Leads',
                align: 'right',
                isSortable: true,
                renderer: data => this.getTotal(data.phoneLeads),
            });
            columnConfig.push({
                dataKey: 'emailLeads',
                width: 1,
                header: 'Email Leads',
                align: 'right',
                isSortable: true,
                renderer: data => this.getTotal(data.emailLeads),
            });
        }
        if (hasClickData) {
            columnConfig.push({
                dataKey: 'totalClicks',
                width: 1,
                header: 'Website Referrals',
                align: 'right',
                isSortable: true,
                renderer: data => this.getTotal(data.totalClicks),
            });
        }
        if (hasLeaseData) {
            columnConfig.push({
                dataKey: 'totalLeases',
                width: 1,
                header: 'Total Leases',
                align: 'right',
                isSortable: true,
                renderer: data => this.getTotal(data.totalLeases),
            });
        }
        if (hasLeadData || hasClickData || hasLeaseData) {
            columnConfig.push({
                width: 1,
                dataKey: 'coreProductCost',
                align: 'right',
                header: 'Contract Spend',
                headerRenderer: () => (
                    <HeaderTooltip
                        title="CONTRACT SPEND"
                        message="Excludes waitlist charges"
                    />
                ),
                renderer: data => this.getCostWithDefault(data.coreProductCost),
            });
        }
        if (hasWaitlistData) {
            columnConfig.push({
                dataKey: 'waitlistedDays',
                width: 1,
                header: 'Waitlist Days',
                align: 'right',
                isSortable: true,
                renderer: data => this.getTotal(data.waitlistedDays),
            });
            columnConfig.push({
                dataKey: 'waitlistCost',
                width: 1,
                header: 'Waitlist Cost',
                align: 'right',
                isSortable: true,
                renderer: data => this.getCostWithDefault(data.waitlistCost),
            });
        }
        if (hasBoostData) {
            columnConfig.push({
                dataKey: 'boostCost',
                width: 1,
                header: 'Boost Spend',
                align: 'right',
                isSortable: true,
                renderer: data => this.getCostWithDefault(data.boostCost),
            });
        }
        if (rocketshipAvailable) {
            columnConfig.push({
                dataKey: 'activeDays',
                width: 1,
                header: 'Active Days',
                align: 'right',
                isSortable: true,
                renderer: data => this.getTotal(data.activeDays),
            });
            columnConfig.push({
                dataKey: 'dailyRate',
                width: 1,
                header: 'Daily Rate',
                align: 'right',
                isSortable: true,
                renderer: data => this.getCostWithDefault(data.dailyRate),
            });
        }
        columnConfig.push({
            dataKey: 'totalCost',
            width: 1,
            header: 'Total Cost',
            align: 'right',
            isSortable: true,
            renderer: data => this.getCostWithDefault(data.totalCost),
        });
        const totalWidth = Math.ceil((columnConfig.length + 1) / 0.8);
        const headerColWidth = totalWidth - columnConfig.length;

        columnConfig.unshift({
            header: 'Advertising Type',
            width: 1,
            renderer: data => (data.listing.productTypes.length > 0
                ? data.listing.productTypes.map((productType, index) => <Text key={index}>{productType}</Text>)
                : '-'),
        });
        columnConfig.unshift({
            dataKey: 'propertyName',
            sortType: 'string',
            width: headerColWidth,
            header: 'Property',
            isSortable: true,
            renderer: this.addressColumnRenderer,
        });
        return columnConfig;
    };

    getTableData(coreProductInvoiceModel, isMobile) {
        let rowList = [];
        let columnConfig = [];

        if (coreProductInvoiceModel && coreProductInvoiceModel.listingInvoices) {
            const hasLeadData = coreProductInvoiceModel.billableLeadCountAvailable;
            const hasClickData = coreProductInvoiceModel.billableClickCountAvailable;
            const hasLeaseData = coreProductInvoiceModel.leaseCountAvailable;
            const hasWaitlistData = coreProductInvoiceModel.waitlistDaysCountAvailable;
            const hasBoostData = coreProductInvoiceModel.billableBoostCountAvailable;
            const { rocketshipAvailable } = coreProductInvoiceModel;
            rowList = this.getRowList(coreProductInvoiceModel, isMobile, hasLeadData, hasClickData, hasLeaseData,
                hasWaitlistData, hasBoostData);
            columnConfig = this.createColumnConfig(hasLeadData, hasClickData, hasLeaseData,
                hasWaitlistData, hasBoostData, rocketshipAvailable);
        }

        return {
            rowList,
            columnConfig,
        };
    }

    render() {
        const {
            coreProductInvoiceModel, isScreenMobile, isLoading, error,
        } = this.props;
        const { rowList, columnConfig } = this.getTableData(coreProductInvoiceModel, isScreenMobile);

        return (
            <div className="AllSpendTable">
                <TableMFH
                    idKey="allSpend"
                    columnConfig={columnConfig}
                    data={isLoading ? [] : rowList}
                    ErrorRowComponent={error ? () => <Error message={error} /> : undefined}
                    isLoading={isLoading}
                    columnDivisions={columnConfig[0].width + columnConfig.length - 1}
                    defaultSortKey={{
                        key: 'propertyName',
                        ascending: true,
                    }}
                />
                {(!isLoading && rowList.length === 0 && !error) &&
                    <Error message="No properties found." />
                }
                {coreProductInvoiceModel.isEstimate && (
                    <Text className="AnalyticsTable-estimate" type="body-tiny" lowlight={true}>
                        These numbers are an estimate. Finalized numbers will appear on your invoice.
                    </Text>
                )}
            </div>
        );
    }
}

AllSpendTable.propTypes = propTypes;

export default AllSpendTable;
